import * as STD                            from '@emersya/mpa-std';
import * as CM                                  from '@emersya/mpa-components-manager';
import { Section, Image }                  from '@emersya/mpa-components';
import ViewerComponent, { NS as ViewerNS } from '../../../Viewer/Viewer';
import { NS as ViewerGraphicsNS }          from '../../../Graphics/ViewerGraphics';

export interface IPlannerWallsTileComponentConfiguration extends Section.Configuration {
	forceBackgroundColor? : string | null;
};

export class PlannerWallsTileComponent extends Section.Component {
	viewerNode : ViewerComponent.ProductInstance | null = null;

	get wallsEditor() {
		return <PlannerWallsEditorNS.Component> this.component_parent?.component_parent;
	};

	async viewerNodeInsert() {
		const product = app.currentProduct;
		if (this.viewerNode || !product || !app.viewer.planner) {
			if (app.isReloading) {
				return;
			}
			this.component_throwUserError('', {});
		}
		if (this.component_isDisabled) {
			await this.component_enable();
		}
		const wallProductSKU = `@wall-${ product.id }`;
		const wallProductSchema = this.wallsEditor.plannerCategory.products.find(x => x.SKU === wallProductSKU);
		if (!wallProductSchema) {
			this.component_throwLibraryError('', {});
		}
		if (app.isReloading) {
			if (!app.previousConfigurationMetadata) {
				this.component_throwLibraryError('', {});
			}
			const vPath = app.viewer.component_getPath();
			const vUI : [ string, ViewerNS.ValueSerialized ] | undefined = <any> app.previousConfigurationMetadata.userInputs.find(
				x => x[ 0 ] === vPath);
			if (!vUI) {
				this.component_throwLibraryError('', {});
			}

			const savedInstance = vUI[ 1 ].productInstances.find(x => x.product === wallProductSKU && x.metadata.id === this.id);
			if (!savedInstance) {
				if (!this.component_isDisabled) {
					await this.component_disable();
				}
				// Wall was disabled and so not saved.
				return;
			}
			const allInstances = Array.from(app.viewer.safe.value.productInstances);
			const node = allInstances.find(x => x.localId === savedInstance.localId);
			if (!node) {
				this.component_throwUserError('', {});
			}
			this.viewerNode = node;
		} else {
			this.viewerNode = await wallProductSchema.instantiate(
				app.viewer.globalLocalId,
				'insert',
				[],
				false,
				{
					id :           this.id,
					customImages : [
						{
							enabled :   false,
							leftRight : 0,
							upDown :    0,
							resize :    1,
							rotate :    0,
							image :     null
						}
					]
				}
			);
		}
		if (!this.viewerNode) {
			this.component_throwLibraryError('', {});
		}
		const ciData = this.viewerNode?.metadata?.customImages?.[ 0 ];
		if (ciData) {
			await (<ViewerGraphicsNS.Component> this.component_find('@:content.graphic-0')).updateValue(<any> ciData);
		}
		this.viewerNode.setName('Wall ' + this.id);
	};

	async viewerNodeRemove() {
		if (app.isReloading) {
			return;
		}
		if (!this.component_isDisabled) {
			await this.component_disable();
		}
		if (!this.viewerNode) {
			return;
		}
		await this.viewerNode.delete();
		this.viewerNode = null;
	};

	getWDH() {
		const p = app.currentProduct;
		if (!p) {
			this.component_throwUserError('', {});
		}
		const dpart = p.getPart([ 'dimensions' ]);
		return {
			width :  Number.parseFloat(dpart.getPart([ 'width' ])?.reference?.value || <string> <any> 1),
			depth :  Number.parseFloat(dpart.getPart([ 'depth' ])?.reference?.value || <string> <any> 1),
			height : Number.parseFloat(dpart.getPart([ 'height' ])?.reference?.value || <string> <any> 1)
		};
	};

	getWallSize() {
		const WDH = this.getWDH();
		const layoutId = this.wallsEditor.layoutId;
		const size = {
			width :  0,
			height : WDH.height - app.getFloorThickness()
		};
		if (this.id === 'back') {
			if (layoutId === 'back') {
				size.width = WDH.width;
			} else if (layoutId === 'left-back-right') {
				size.width = WDH.width + (this.getWallThickness() * 2);
			} else {
				size.width = WDH.width + this.getWallThickness();
			}
		} else {
			size.width = WDH.depth;
		}
		return size;
	};

	getVisibleWallSize() {
		const WDH = this.getWDH();
		const size = {
			width :  0,
			height : WDH.height - app.getFloorThickness()
		};
		if (this.id === 'back') {
			size.width = WDH.width;
		} else {
			size.width = WDH.depth;
		}
		return size;
	};

	getGraphicsLocalId(customImageId : string) {
		void customImageId;
		if (!this.viewerNode) {
			return -1;
		}
		return <number> this.viewerNode.idMapping[ 'customImage' ];
	};

	getWallImageCustomizationZone() {
		return {
			size : {
				x : 0,
				y : 0
			}
		};
	};

	getGraphicsSizeX(customImageId : string) {
		void customImageId;
		if (!this.viewerNode) {
			return -1;
		}
		return this.getWallImageCustomizationZone().size.x;
	};

	getGraphicsSizeY(customImageId : string) {
		if (!this.viewerNode) {
			return -1;
		}
		void customImageId;
		return this.getWallImageCustomizationZone().size.y;
	};

	getGraphicsMesh(customImageId : string) {
		if (!this.viewerNode) {
			return -1;
		}
		void customImageId;
		return 'PATCH_CLOISON';
	};

	getGraphicsMaterial() {
		return {
			name :          'PATCH_CLOISON',
			configuration : {
				enabled :  'PATCH_CLOISON',
				disabled : 'off'
			}
		};
	};

	getGraphicsProductInstance() {
		return this.viewerNode;
	};

	getWallThickness() {
		// TODO : DEHARDCODE
		return 0.05;
	};

	computeMatrix() : ViewerComponent.TMatrix {
		const groundSize = this.getWDH();
		const wallSize = this.getWallSize();
		const layoutId = this.wallsEditor.layoutId;
		if (this.id === 'left') {
			if (layoutId === 'left-back-right') {
				return (ViewerComponent.mtx(
					[ -(groundSize.width / 2), 0, groundSize.depth / 2 ],
					[ wallSize.width, wallSize.height, 1 ],
					90
				));
			} else {
				return (ViewerComponent.mtx(
					[ -(groundSize.width / 2), 0, groundSize.depth / 2 ],
					[ wallSize.width, wallSize.height, 1 ],
					90
				));
			}
		} else if (this.id === 'back') {
			if (layoutId === 'back') {
				return (ViewerComponent.mtx(
					[ -groundSize.width / 2, 0, -(groundSize.depth / 2) ],
					[ wallSize.width, wallSize.height, 1 ],
					0
				));
			} else if (layoutId === 'back-left') {
				return (ViewerComponent.mtx(
					[ -groundSize.width / 2 - this.getWallThickness(), 0, -(groundSize.depth / 2) ],
					[ wallSize.width, wallSize.height, 1 ],
					0
				));
			} else if (layoutId === 'back-right') {
				return ViewerComponent.mtx(
					[ -groundSize.width / 2, 0, -(groundSize.depth / 2) ],
					[ wallSize.width, wallSize.height, 1 ],
					0
				);
			} else {
				return ViewerComponent.mtx(
					[ -groundSize.width / 2 - this.getWallThickness(), 0, -(groundSize.depth / 2) ],
					[ wallSize.width, wallSize.height, 1 ],
					0
				);
			}
		} else { // Right
			if (layoutId === 'left-back-right') {
				return (ViewerComponent.mtx(
					[ groundSize.width / 2, 0, -groundSize.depth / 2 ],
					[ wallSize.width, wallSize.height, 1 ],
					-90
				));
			} else {
				return (ViewerComponent.mtx(
					[ groundSize.width / 2, 0, -groundSize.depth / 2 ],
					[ wallSize.width, wallSize.height, 1 ],
					-90
				));
			}
		}
	};

	async viewerNodeUpdate() {
		if (!this.viewerNode || app.isReloading) {
			return;
		}
		const batch : ViewerComponent.IBatchDescriptor = [
			[
				'setProductNodesInstancesMatrices',
				[
					{
						localId : this.viewerNode.localId,
						matrix :  this.computeMatrix()
					}
				]
			]
		];
		await app.viewer.batch(batch);
	};

	getMenuConfigurator() {
		return this.component_find('@:content');
	};

	constructor(
		descriptor : ConstructorParameters<typeof CM.Component> [ 0 ],
		configuration : IPlannerWallsTileComponentConfiguration
	)
	{
		super(descriptor, configuration);
		const translationTitle = configuration['@translations']?.title;
		if (!translationTitle) {
			this.component_throwUserError('Missing translations for planner walls editor ' + this.id, {});
		}
		const tr = STD.translationsManager.languages.reduce<Record<string, string>>((a, c) => {
			a[ c ] = 'icon header';
			return a;
		}, {});
		configuration[ '@innerConfigurations' ] = {
			'header' : {
				'@innerConfigurations' : {
					'title' :      {
						'@translations' : {
							'text' : translationTitle
						}
					},
					'headerIcon' : <Image.Configuration> {
						'@translations' : {
							'name' : tr
						},
						'URL' :           '/bundles/EmersyaWebsitePro/images/apps/_mpa/engine/0.0.0/arrow.svg'
					}
				}
			},
			content :  {
				'@innerConfigurations' : {
					'graphic-0' : <ViewerGraphicsNS.Configuration> {
						forceBackgroundColor : configuration.forceBackgroundColor
					}
				}
			}
		};
	}
}

export namespace NS {
	export const Component = PlannerWallsTileComponent;
	export type Component = PlannerWallsTileComponent;
	export type Configuration = IPlannerWallsTileComponentConfiguration;
}
import manifestXMLSource                   from './PlannerWallsProductTile.xml';
import './PlannerWallsProductTile.scss';
import { NS as PlannerWallsEditorNS }      from '../Editor/PlannerWallsEditor';

CM.Manager.declare({
	id :       'viewer-planner-walls-productTile',
	type :     PlannerWallsTileComponent,
	manifest : manifestXMLSource
});
